/**
 * What became of a bid, in words a founder can act on (canon APP-010).
 *
 * The wording lives here rather than in the panel for the reason `./placement-source.ts`
 * gives: it is the stable part of the contract keyed on a code, and one sentence per outcome
 * is easier to keep honest — and to test against canon — in one place than scattered through
 * a component's branches.
 *
 * One rule governs every sentence below. **Say where the money went.** Canon § 5 reserves
 * promotional credit while a bid stands, consumes it only for a winning finalized placement,
 * and returns it to available balance when the bid does not win. A founder reading this
 * panel is usually reading it because their balance moved and they want to know why, so the
 * credit consequence is part of the outcome rather than a footnote elsewhere.
 *
 * A second rule follows from canon § 12. A won position is **sponsored placement** — it says
 * the founder bid highest that day, not that Dealbid judged their deal best — so no sentence
 * here congratulates a deal on its quality.
 */
import type { SellerBidOutcome } from "../api/types";

export interface BidOutcomeCopy {
  /** The short verdict, always shown as a word rather than as a colour or a mark. */
  label: string;
  /** One sentence saying what happened to the bid and to the credit it reserved. */
  note: string;
  /** Whether the day this bid competed for has been decided. */
  settled: boolean;
}

const COPY: Record<SellerBidOutcome, BidOutcomeCopy> = {
  standing: {
    label: "Standing",
    note: "This bid is still competing. Its credit is reserved while it stands, and is only spent if it wins a position.",
    settled: false,
  },
  won: {
    label: "Won a position",
    note: "This bid won a Top 10 position for that display day, and its credit was consumed when the day was finalised. It is sponsored placement — it says you bid highest, not that Dealbid ranked your deal best.",
    settled: true,
  },
  lost: {
    label: "Did not place",
    note: "This bid competed for that day and did not win a position. The credit it reserved returned to your available balance — nothing was spent.",
    settled: true,
  },
  withdrawn: {
    label: "Withdrawn",
    note: "This bid was taken back before that day was decided, so it did not compete for a position and holds no reserved credit.",
    settled: true,
  },
};

export function describeBidOutcome(outcome: SellerBidOutcome): BidOutcomeCopy {
  return COPY[outcome];
}

/**
 * The one sentence a `standing` bid gets in place of a result, chosen on the auction's own
 * state rather than on the outcome alone.
 *
 * `contracts/API.md` ships `auctionFinalized` beside `outcome` precisely so "standing" and
 * "that day has not run" stay separately checkable. They should always agree — finalization
 * settles every bid in an auction — so the second sentence below describes a response that
 * is visibly inconsistent, and says only what can be seen rather than inventing a reason for
 * it.
 */
export function describeStanding(auctionFinalized: boolean): string {
  return auctionFinalized
    ? "That display day has been settled, but Dealbid has not recorded a result for this bid yet."
    : "That display day has not been decided yet, so there is no result to report.";
}
